import React, { ReactNode, useMemo, useState } from 'react';
import { KeycloakProvider } from '@react-keycloak/web';
import Keycloak from 'keycloak-js';
import { useTimeoutFn } from 'react-use';

import { DataPointsAuthEvent } from '#coreComponents/DevelopmentNotifier/listeners/dataPointsAuth';
import { useTracking, EventType } from '#core/Tracking';
import { useEnvironment } from '#core/Environment';

import { AuthContract } from '../contexts/AuthContext';
import AuthProvider from './AuthProvider';

const KEYCLOAK_INIT_TIMEOUT = 15000;

type AuthProviderProps = {
  children: (data: AuthContract) => ReactNode | null;
};

const Auth: React.FC<AuthProviderProps> = (props) => {
  const [keycloakInitialised, setKeycloakInitialised] = useState(false);
  const [initializeFailed, setInitializeFailed] = useState(false);
  const { keycloakConfig } = useEnvironment();
  const keycloak = useMemo(() => Keycloak(keycloakConfig), [keycloakConfig]);
  const { trackEvent } = useTracking();
  const [keycloakError, setKeycloakError] = useState<Error>();

  useTimeoutFn(() => {
    if (!keycloakInitialised) {
      setInitializeFailed(true);
    }
  }, KEYCLOAK_INIT_TIMEOUT);

  if (initializeFailed) {
    throw new Error('Failed to contact authentication server');
  }

  if (keycloakError) {
    throw keycloakError;
  }

  return (
    <KeycloakProvider
      keycloak={keycloak}
      initConfig={{
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: `${window.location.origin}/datapoints/silent-check-sso.html`,
      }}
      onEvent={(event, error) => {
        let eventType: DataPointsAuthEvent['type'];
        let eventMessage: DataPointsAuthEvent['message'];
        let eventErrorMessage: DataPointsAuthEvent['errorMessage'];

        switch (event) {
          case 'onAuthSuccess':
            eventType = 'success';
            eventMessage = 'Auth succeeded';
            eventErrorMessage = error?.error || '';
            trackEvent({
              type: EventType.LOGIN,
              payload: {
                id: 'auth',
                metadata: {},
              },
            });
            break;
          case 'onAuthError':
            eventType = 'error';
            eventMessage = 'Auth failed';
            eventErrorMessage = error?.error || '';
            setKeycloakError(new Error(error?.error));
            trackEvent({
              type: EventType.LOGIN_FAIL,
              payload: {
                id: 'auth',
                metadata: {},
              },
            });
            break;
          case 'onAuthRefreshSuccess':
            eventType = 'success';
            eventMessage = 'Auth token refreshed';
            eventErrorMessage = error?.error || '';
            break;
          case 'onAuthRefreshError':
            eventType = 'error';
            eventMessage = 'Auth token refresh failed';
            eventErrorMessage = error?.error || '';
            break;
          case 'onInitError':
            eventType = 'error';
            eventMessage = 'Auth init failed';
            eventErrorMessage = error?.error || '';
            setKeycloakError(new Error(error?.error));
            break;
          case 'onReady':
            setKeycloakInitialised(true);
            break;
          case 'onTokenExpired':
          case 'onAuthLogout':
          default:
        }
        setTimeout(() => {
          if (eventMessage) {
            document.dispatchEvent(
              new CustomEvent<DataPointsAuthEvent>('datapoints-auth', {
                detail: {
                  type: eventType,
                  message: eventMessage,
                  errorMessage: eventErrorMessage,
                },
              }),
            );
          }
        }, 0);
      }}
    >
      <AuthProvider {...props} />
    </KeycloakProvider>
  );
};

export default Auth;
